import { Component, Input } from '@angular/core';
import { RouterModule } from '@angular/router';

import { AdvertiserContact, BusinessService } from '../../services/business.service';

/**
 * SCRUM-287 — "פרטי העסק" on an ad: business name + logo, license, website
 * link, and a contact-person row with a call button.
 *
 * Used on the ad page and inside the "צור קשר" menu. Renders only what the
 * server sent (the business picks the fields), and nothing at all when the
 * business has not switched the block on.
 */
@Component({
  selector: 'app-business-contact',
  standalone: true,
  imports: [RouterModule],
  template: `
    @if (contact) {
      <div class="bc" [class.bc--compact]="compact" dir="rtl">
        @if (hasBusinessRow) {
          <div class="bc-biz">
            @if (contact.logo) {
              <a class="bc-logo" [routerLink]="sellerId ? ['/seller', sellerId] : null">
                <img [src]="contact.logo" [alt]="contact.business_name || 'לוגו העסק'" />
              </a>
            }
            <div class="bc-text">
              @if (contact.business_name) {
                <div class="bc-title">
                  @if (label) { <span class="bc-title__label">{{ label }}:</span> }
                  {{ contact.business_name }}
                </div>
              }
              @if (contact.license_number) {
                <div class="bc-license">
                  @if (contact.license_verified) {
                    <i class="mdi mdi-check-circle bc-license__ok" [attr.title]="contact.verified_text || 'רישיון מאומת'"></i>
                  }
                  <span>מס׳ רישיון <bdi dir="ltr">{{ contact.license_number }}</bdi></span>
                  @if (!contact.license_verified) { <span class="bc-license__pending">· טרם אומת</span> }
                </div>
              }
              @if (contact.website) {
                <a class="bc-site" [href]="contact.website" target="_blank" rel="noopener noreferrer nofollow"
                   (click)="onSite($event)">
                  לאתר העסק <i class="mdi mdi-chevron-left" aria-hidden="true"></i>
                </a>
              }
            </div>
          </div>
        }
        @if (hasPersonRow) {
          @if (hasBusinessRow) { <hr class="bc-sep" /> }
          <div class="bc-person">
            <span class="bc-avatar">
              @if (contact.person_avatar) {
                <img [src]="contact.person_avatar" [alt]="contact.person_name || ''" />
              } @else if (contact.person_name) {
                {{ contact.person_name.charAt(0) }}
              } @else {
                <i class="mdi mdi-account" aria-hidden="true"></i>
              }
            </span>
            <div class="bc-text">
              @if (contact.person_name) { <div class="bc-person__name">{{ contact.person_name }}</div> }
              @if (contact.phone) { <div class="bc-person__phone" dir="ltr">{{ contact.phone }}</div> }
            </div>
            @if (contact.phone) {
              <a class="bc-call" [href]="'tel:' + telPhone" (click)="onCall($event)" aria-label="התקשר לעסק" title="התקשר">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                </svg>
              </a>
            }
          </div>
        }
      </div>
    }
  `,
  styles: [`
    :host { display: block; }
    .bc { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 14px 16px; text-align: right;
          font-family: inherit; color: #0f172a; }
    .bc--compact { border: none; border-radius: 0; padding: 12px 14px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; }
    .bc-biz, .bc-person { display: flex; align-items: center; gap: 12px; }
    .bc-text { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
    .bc-logo { flex: 0 0 auto; width: 64px; height: 64px; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden;
               display: block; background: #fff; }
    .bc-logo img { width: 100%; height: 100%; object-fit: contain; display: block; }
    .bc-title { font-size: 17px; font-weight: 700; line-height: 1.3; overflow-wrap: anywhere; }
    .bc-title__label { font-weight: 500; color: #334155; }
    .bc-license { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; font-size: 13px; color: #475569; }
    .bc-license__ok { color: #16a34a; font-size: 15px; line-height: 1; }
    .bc-license__pending { color: #92400e; font-weight: 600; }
    .bc-site { align-self: flex-start; display: inline-flex; align-items: center; gap: 2px; font-size: 14px; font-weight: 700;
               color: #0f172a; text-decoration: none; }
    .bc-site:hover { text-decoration: underline; }
    .bc-site .mdi { font-size: 18px; line-height: 1; }
    .bc-sep { border: none; border-top: 1px solid #e2e8f0; margin: 12px 0; }
    .bc-avatar { flex: 0 0 auto; width: 48px; height: 48px; border-radius: 50%; overflow: hidden; background: #eff6ff;
                 color: #2563eb; font-weight: 700; font-size: 18px; display: flex; align-items: center; justify-content: center; }
    .bc-avatar img { width: 100%; height: 100%; object-fit: cover; }
    .bc-person__name { font-size: 16px; font-weight: 700; }
    .bc-person__phone { font-size: 13px; color: #475569; text-align: right; }
    .bc-call { flex: 0 0 auto; width: 46px; height: 46px; border-radius: 50%; border: 1.5px solid #e8723a; color: #e8723a;
               display: flex; align-items: center; justify-content: center; background: #fff; transition: background .15s; }
    .bc-call:hover { background: #fff4ee; }
    .bc-call:focus-visible, .bc-site:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; }
    .bc--compact .bc-logo { width: 44px; height: 44px; }
    .bc--compact .bc-title { font-size: 14px; }
    .bc--compact .bc-avatar { width: 36px; height: 36px; font-size: 15px; }
    .bc--compact .bc-person__name { font-size: 14px; }
    .bc--compact .bc-call { width: 38px; height: 38px; }
    .bc--compact .bc-sep { margin: 10px 0; }
  `],
})
export class BusinessContactComponent {
  @Input() contact: AdvertiserContact | null = null;
  /** "מתווך" / "סוחר רכב" — from the listing's advertiser block. */
  @Input() label: string | null = null;
  @Input() adId: number | null = null;
  @Input() sellerId: number | null = null;
  @Input() compact = false;

  constructor(private business: BusinessService) {}

  get hasBusinessRow(): boolean {
    const c = this.contact;
    return !!c && !!(c.business_name || c.logo || c.license_number || c.website);
  }

  get hasPersonRow(): boolean {
    const c = this.contact;
    return !!c && !!(c.person_name || c.phone);
  }

  get telPhone(): string {
    return (this.contact?.phone || '').replace(/[^\d+]/g, '');
  }

  /** Measured only — a click is never a billable event at this stage. */
  onSite(event: Event): void {
    event.stopPropagation();
    this.business.track('website_click', { ad_id: this.adId });
  }

  onCall(event: Event): void {
    event.stopPropagation();
    this.business.track('phone_tap', { ad_id: this.adId });
  }
}
